import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { may, send } from "../api/auth";
import {
  console_,
  type AvailabilityExceptionKind,
  type AvailabilityException,
  type AvailabilityPattern,
  type AvailabilityPayload,
  type AvailabilityPreference,
  type AvailabilityWindow,
} from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { AddButton } from "../design/AddLink";
import { Tabs } from "../design/Tabs";
import { Notice } from "../design/Notice";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// AVAILABILITY — V12. WHEN A PERSON CAN WORK.
//
// TWO SCREENS OUT OF ONE FILE, because they render the same three lists from
// the same endpoint and differ only in who is looking:
//
//   WorkerBenchAvailability   the console. A scheduler on the phone to a
//                             person on the bench, writing down what they said.
//   MyAvailability            the worker app. The same three lists, at worker
//                             density, plus the one switch that is nobody's
//                             but theirs.
//
// THE RULES THIS FILE FOLLOWS:
//
//   1. IT ASKS NOBODY WHY. There is no reason field, no note, no free text of
//      any kind — because "why are you unavailable on the 14th" is how a
//      scheduling product ends up holding a hospital appointment. The database
//      has no column for it and this screen offers no box for it. Absolute rule
//      5, and Directive 2024/2831.
//
//   2. IT DOES NO CLOCK ARITHMETIC. Not one millisecond multiplied by anything.
//      Every window comes back from the server already converted in the zone's
//      own rules, with its elapsed minutes, and `crosses_midnight` arrives as a
//      flag rather than being derived from comparing two strings. The browser
//      has no IANA arithmetic and the naive version is wrong twice a year.
//
//      THIS PARAGRAPH DELIBERATELY SPELLS NO PRODUCT OUT. verify-V12.sh §7
//      greps the source for that shape, and V11 shipped a comment that broke
//      its own guard by quoting the string it claimed was absent.
//
//   3. IT NAMES NEITHER EXCEPTION KIND. The two kinds arrive with
//      `makes_available`, which is the only question this screen asks of them.
//      verify-V12.sh §7 greps the source for their ids.
//
//   4. STATUS IS NEVER COLOUR ALONE. Every window and every exception carries a
//      glyph and a word. This is read on a cracked phone, in the rain,
//      at 06:00.
//
//   5. THE MARKETPLACE SWITCH SAYS WHAT IT DOES IN BOTH POSITIONS, and the
//      sentence comes from the server so the screen and the API cannot drift
//      apart about what was turned on.
// ---------------------------------------------------------------------------

// A failed read keeps its status (design/LoadFailed); "no bench record on this account" is a fact, not a fault.
type Loaded<T> =
  | { state: "loading" }
  | { state: "none" }
  | { state: "error"; failure: Failure }
  | { state: "ready"; data: T };

/**
 * Monday first, and the labels are NOT a taxonomy — a weekday is calendrical
 * rather than configurable, and no vertical has ever needed a different set.
 * The INDEX is what the API takes: 0 is Monday, matching ISO 8601 minus one.
 */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
type Translate = ReturnType<typeof useT>["t"];

function weekdaySentence(days: number[], t: Translate): string {
  if (days.length === 7) return t("Every day");
  return days
    .map((d) => WEEKDAYS[d])
    .map((day) => (day ? t(day) : "?"))
    .join(", ");
}

/** Minutes as a person says them. No division that could round a shift away. */
function lengthOf(minutes: number, t: Translate): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return t("{m} min", { m: rest });
  if (rest === 0) return t("{h} hr", { h: hours });
  return t("{h} hr {m} min", { h: hours, m: rest });
}

// ===========================================================================
// THE SHARED BODY
// ===========================================================================

function PatternRows({
  patterns,
  onRemove,
  canWrite,
}: {
  patterns: AvailabilityPattern[];
  onRemove: ((id: string) => void) | null;
  canWrite: boolean;
}) {
  const { t } = useT();
  if (patterns.length === 0) {
    return (
      <p className="muted">
        {t("No weekly pattern yet. A pattern is the ordinary week — the days and hours somebody can normally work.")}
      </p>
    );
  }
  return (
    <ul className="stack" role="list">
      {patterns.map((pattern) => (
        <li key={pattern.id} className="row">
          <div>
            <strong>{weekdaySentence(pattern.weekdays, t)}</strong>{" "}
            <span>
              {t("{from} to {to}", { from: pattern.starts_local.slice(0, 5), to: pattern.ends_local.slice(0, 5) })}
            </span>
            {pattern.crosses_midnight ? (
              // THE FLAG, NOT A COMPARISON. The server already knows, and a
              // screen that worked it out from two strings would get 00:00
              // wrong on the one shift where it matters most.
              <span className="muted">{t(" · finishes the next morning")}</span>
            ) : null}
            <div className="muted">
              {t("{zone} · from {date}", { zone: pattern.time_zone, date: pattern.effective_from })}
              {pattern.effective_to ? t(" until {date}", { date: pattern.effective_to }) : ""}
            </div>
          </div>
          {canWrite && onRemove ? (
            <button type="button" className="btn btn-quiet" onClick={() => onRemove(pattern.id)}>
              {t("Remove")}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ExceptionRows({
  exceptions,
  onRemove,
  canWrite,
}: {
  exceptions: AvailabilityException[];
  onRemove: ((id: string) => void) | null;
  canWrite: boolean;
}) {
  const { t } = useT();
  if (exceptions.length === 0) {
    return <p className="muted">{t("No dated changes. Add one for a day that does not follow the usual week.")}</p>;
  }
  return (
    <ul className="stack" role="list">
      {exceptions.map((exception) => (
        <li key={exception.id} className="row">
          <div>
            {/* THE PROPERTY DECIDES THE TONE, NOT THE ID. `makes_available` is
                the only question this screen asks, so a third kind added by a
                later migration renders correctly with no edit here. */}
            <StatusChip
              tone={exception.kind_makes_available ? "live" : "attention"}
              label={exception.kind_label}
            />{" "}
            <strong>{exception.on_date}</strong>{" "}
            <span>
              {exception.is_whole_day
                ? t("all day")
                : t("{from} to {to}", {
                    from: (exception.starts_local ?? "").slice(0, 5),
                    to: (exception.ends_local ?? "").slice(0, 5),
                  })}
            </span>
            <div className="muted">{exception.time_zone}</div>
          </div>
          {canWrite && onRemove ? (
            <button type="button" className="btn btn-quiet" onClick={() => onRemove(exception.id)}>
              {t("Remove")}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function PreferenceRows({ preference }: { preference: AvailabilityPreference | null }) {
  const { t } = useT();
  if (!preference) {
    return (
      <p className="muted">
        {t(
          "Nothing stated. These are preferences and never limits — the working-time rules that are law are held separately.",
        )}
      </p>
    );
  }
  // NULL IS "NOT STATED" AND IS RENDERED AS SUCH. A dash rather than a zero,
  // because a scheduler reading "0 hours" stops offering work to somebody who
  // simply never filled the form in.
  const rows: Array<[string, string]> = [
    [t("Hours a week at most"), preference.max_hours_per_week === null ? "—" : String(preference.max_hours_per_week)],
    [t("Shifts a week at most"), preference.max_shifts_per_week === null ? "—" : String(preference.max_shifts_per_week)],
    [t("Notice wanted"), preference.min_notice_hours === null ? "—" : t("{h} hr", { h: preference.min_notice_hours })],
    [t("Travel at most"), preference.max_travel_minutes === null ? "—" : t("{m} min", { m: preference.max_travel_minutes })],
  ];
  return (
    <dl className="facts">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function WindowRows({ windows }: { windows: AvailabilityWindow[] }) {
  const { me } = useSession();
  const { t } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  if (windows.length === 0) {
    return <p className="muted">{t("Nothing in this period. The pattern and the dated changes add up to no time at all.")}</p>;
  }
  return (
    <ul className="stack" role="list">
      {windows.map((w) => (
        <li key={`${w.starts_at}-${w.ends_at}`} className="row">
          <div>
            <StatusChip tone="live" label={t("Free")} />{" "}
            <span>{t("{from} to {to}", { from: whenIn(w.starts_at, orgZone), to: whenIn(w.ends_at, orgZone) })}</span>
          </div>
          {/* THE ELAPSED LENGTH, FROM THE SERVER. The Saturday that begins
              24 October 2026 at 18:00 in London reads 9 hr here and the one in
              March reads 7 hr, and this screen did no arithmetic to say so. */}
          <span className="muted">{lengthOf(w.minutes, t)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The add-a-pattern form.
 *
 * THE TIME ZONE IS ASKED FOR AND NOT GUESSED. `Intl.DateTimeFormat().resolvedOptions().timeZone`
 * is the browser's zone, which is where the person is sitting rather than where
 * the work is — and somebody filling this in on a train is exactly the case
 * that produces a call time an hour out. It is pre-filled from the browser as a
 * SUGGESTION and shown as an editable field, so a wrong one is visible.
 */
function PatternForm({ onSubmit, busy }: { onSubmit: (payload: Record<string, unknown>) => void; busy: boolean }) {
  const { t } = useT();
  const [days, setDays] = useState<number[]>([]);
  const [startsLocal, setStartsLocal] = useState("18:00");
  const [endsLocal, setEndsLocal] = useState("02:00");
  const [zone, setZone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";
    } catch {
      return "Europe/London";
    }
  });
  const [from, setFrom] = useState("");

  function toggle(day: number) {
    setDays((current) => (current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort()));
  }

  const ready = days.length > 0 && from !== "" && zone.trim() !== "";

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready || busy) return;
        onSubmit({
          time_zone: zone.trim(),
          weekdays: days,
          starts_local: startsLocal,
          ends_local: endsLocal,
          effective_from: from,
        });
      }}
    >
      <fieldset className="form-section">
        <legend>{t("Which days")}</legend>
        <div className="chip-row">
          {WEEKDAYS.map((label, index) => (
            <label key={label} className="chip-check">
              <input type="checkbox" checked={days.includes(index)} onChange={() => toggle(index)} />
              <span>{t(label)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="row">
        <label>
          <span>{t("From")}</span>
          <input type="time" value={startsLocal} onChange={(e) => setStartsLocal(e.target.value)} required />
        </label>
        <label>
          <span>{t("Until")}</span>
          <input type="time" value={endsLocal} onChange={(e) => setEndsLocal(e.target.value)} required />
        </label>
      </div>
      {endsLocal <= startsLocal ? (
        <p className="muted">{t("This finishes the next morning. That is allowed — a night shift is the normal case here.")}</p>
      ) : null}

      <label>
        <span>{t("Time zone")}</span>
        <input value={zone} onChange={(e) => setZone(e.target.value)} required />
      </label>
      <p className="muted">
        {t(
          "A named zone such as Europe/London, never an offset. An offset is a value at one moment; a zone is a rule over time, and the difference is an hour on two days a year.",
        )}
      </p>

      <label>
        <span>{t("Starting from")}</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
      </label>

      <button type="submit" className="btn" disabled={!ready || busy}>
        {t("Add this pattern")}
      </button>
    </form>
  );
}

/**
 * The four stated preferences, as a form.
 *
 * FOUR NUMBERS AND NOTHING ELSE, AND THAT IS THE ENTIRE DESIGN. Every field
 * here is an integer with a unit. There is no "anything else we should know"
 * box, because that box is where somebody types a diagnosis, and a form that
 * invites it once holds it forever.
 *
 * BLANK MEANS NOT STATED, NOT ZERO. An empty field sends null, and null renders
 * as a dash. A zero would mean "will work no hours", which is a sentence nobody
 * intended to write and a scheduler would act on.
 *
 * NEITHER SCREEN CALLS THIS A LIMIT. V31 owns the statutory limits, which are
 * law and are enforced elsewhere. These are what a person would prefer, and the
 * two must never be rendered by the same component.
 */
function PreferenceForm({
  preference,
  onSubmit,
  onCancel,
  busy,
}: {
  preference: AvailabilityPreference | null;
  onSubmit: (payload: Record<string, unknown>) => void;
  onCancel?: () => void;
  busy: boolean;
}) {
  const { t } = useT();
  const asText = (n: number | null | undefined) => (n === null || n === undefined ? "" : String(n));
  const [hours, setHours] = useState(() => asText(preference?.max_hours_per_week));
  const [shifts, setShifts] = useState(() => asText(preference?.max_shifts_per_week));
  const [notice, setNotice] = useState(() => asText(preference?.min_notice_hours));
  const [travel, setTravel] = useState(() => asText(preference?.max_travel_minutes));

  // Trimmed-empty becomes null on the wire. `Number("")` is 0, which is the
  // exact confusion this function exists to prevent.
  const orNull = (raw: string) => (raw.trim() === "" ? null : Number(raw.trim()));

  const fields: Array<[string, string, string, (v: string) => void, string]> = [
    [t("Hours a week at most"), "hours", hours, setHours, t("Leave blank if there is no figure.")],
    [t("Shifts a week at most"), "shifts", shifts, setShifts, t("Counted as shifts, not hours.")],
    [t("Notice wanted, in hours"), "notice", notice, setNotice, t("How much warning before a shift starts.")],
    [t("Travel at most, in minutes"), "travel", travel, setTravel, t("Each way, door to door.")],
  ];

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy) return;
        onSubmit({
          max_hours_per_week: orNull(hours),
          max_shifts_per_week: orNull(shifts),
          min_notice_hours: orNull(notice),
          max_travel_minutes: orNull(travel),
        });
      }}
    >
      {fields.map(([label, name, value, set, help]) => (
        <label key={name}>
          <span>{label}</span>
          <input type="number" min="0" step="1" value={value} onChange={(e) => set(e.target.value)} />
          <span className="muted">{help}</span>
        </label>
      ))}
      <div className="row">
        <button type="submit" className="btn" disabled={busy}>
          {t("Save what they asked for")}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn-quiet" onClick={onCancel}>
            {t("Cancel")}
          </button>
        ) : null}
      </div>
    </form>
  );
}

/** The add-a-dated-change form. No reason field, and there never will be one. */
function ExceptionForm({
  kinds,
  onSubmit,
  busy,
}: {
  kinds: AvailabilityExceptionKind[];
  onSubmit: (payload: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const { t } = useT();
  const [kind, setKind] = useState("");
  const [onDate, setOnDate] = useState("");
  const [wholeDay, setWholeDay] = useState(true);
  const [startsLocal, setStartsLocal] = useState("09:00");
  const [endsLocal, setEndsLocal] = useState("17:00");
  const [zone, setZone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";
    } catch {
      return "Europe/London";
    }
  });

  const ready = kind !== "" && onDate !== "" && zone.trim() !== "";

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready || busy) return;
        onSubmit({
          kind,
          time_zone: zone.trim(),
          on_date: onDate,
          // BOTH OR NEITHER. One of two would store as "from 09:00 until
          // unspecified" and expand into something nobody wrote.
          ...(wholeDay ? {} : { starts_local: startsLocal, ends_local: endsLocal }),
        });
      }}
    >
      <label>
        <span>{t("What kind")}</span>
        <select value={kind} onChange={(e) => setKind(e.target.value)} required>
          <option value="">{t("Choose…")}</option>
          {kinds.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label}
            </option>
          ))}
        </select>
      </label>

      <label>
        <span>{t("Which day")}</span>
        <input type="date" value={onDate} onChange={(e) => setOnDate(e.target.value)} required />
      </label>

      <label className="check">
        <input type="checkbox" checked={wholeDay} onChange={(e) => setWholeDay(e.target.checked)} />
        <span>{t("The whole day")}</span>
      </label>

      {wholeDay ? null : (
        <div className="row">
          <label>
            <span>{t("From")}</span>
            <input type="time" value={startsLocal} onChange={(e) => setStartsLocal(e.target.value)} required />
          </label>
          <label>
            <span>{t("Until")}</span>
            <input type="time" value={endsLocal} onChange={(e) => setEndsLocal(e.target.value)} required />
          </label>
        </div>
      )}

      <label>
        <span>{t("Time zone")}</span>
        <input value={zone} onChange={(e) => setZone(e.target.value)} required />
      </label>

      <button type="submit" className="btn" disabled={!ready || busy}>
        {t("Add this change")}
      </button>
    </form>
  );
}

// ===========================================================================
// THE HOOK BOTH SCREENS USE
// ===========================================================================

function useAvailability(workerId: string) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [loaded, setLoaded] = useState<Loaded<AvailabilityPayload>>({ state: "loading" });
  const [windows, setWindows] = useState<AvailabilityWindow[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!workerId) {
      setLoaded({ state: "none" });
      return;
    }
    const { status, body } = await send<AvailabilityPayload & { error?: { message?: string } }>(
      console_.getAvailability(apiBase, workerId),
    );
    if (status === 200 && body.patterns) {
      setLoaded({ state: "ready", data: body });
      // NO DATES ARE SENT, AND THAT IS THE POINT. The endpoint defaults the
      // period in SQL. A screen that computed `now + 28 days` would be making a
      // clock sum in JavaScript — safe in this instance and one edit away from
      // not being, which is exactly how the defect this slice exists to prevent
      // arrives. verify-V12.sh greps for it, so there is none at all.
      const expansion = await send<{ windows?: AvailabilityWindow[] }>(
        console_.getAvailabilityWindows(apiBase, workerId),
      );
      setWindows(expansion.status === 200 ? (expansion.body.windows ?? []) : []);
    } else {
      setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
    }
  }, [apiBase, workerId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function mutate(run: () => Promise<{ status: number; body: { error?: { message?: string } } }>, failed: string): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setNotice(null);
    try {
      const { status, body } = await run();
      if (status !== 200 && status !== 201) {
        setNotice(refusal(body.error) ?? failed);
        return false;
      }
      await load();
      return true;
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
      return false;
    } finally {
      setBusy(false);
    }
  }

  return { loaded, windows, notice, busy, load, mutate, setNotice };
}

// ===========================================================================
// THE CONSOLE SCREEN
// ===========================================================================
export function WorkerBenchAvailability({ embedded = false }: { embedded?: boolean } = {}) {
  const { apiBase, me } = useSession();
  const { t } = useT();
  const { id = "" } = useParams();
  const { loaded, windows, notice, busy, load, mutate } = useAvailability(id);
  const canWrite = me ? may(me, "worker.manage") : false;
  const [addingPattern, setAddingPattern] = useState(false);
  const [addingChange, setAddingChange] = useState(false);
  const [editingPref, setEditingPref] = useState(false);

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;

  if (loaded.state === "error" || loaded.state === "none") {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Availability")}</h2>
        </div>
        {loaded.state === "none" ? (
          <Notice tone="neutral">{t("There is no bench record on this account yet.")}</Notice>
        ) : (
          <LoadFailed failure={loaded.failure} fallback={t("We could not load this availability.")} onRetry={() => void load()} />
        )}
        <Link className="btn btn-quiet" to="/console/workers">
          {t("Back to the bench")}
        </Link>
      </section>
    );
  }

  const { worker, patterns, exceptions, preference, exception_kinds } = loaded.data;

  return (
    <>
      {embedded ? null : (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("{name} availability", { name: `${worker.given_name} ${worker.family_name}` })}</h2>
          <Link className="btn btn-quiet" to={`/console/workers/${worker.id}`}>
            {t("Back to the record")}
          </Link>
        </div>
        {notice ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone="failed" label={t("Not saved")} />
            <span>{notice}</span>
          </div>
        ) : null}
        <p className="muted">
          {t("What this person told you they can work. It is theirs rather than yours — you can write down what they said, and they can change it themselves at any time.")}
        </p>
      </section>
      )}

      <section className="panel">
        <div className="panel-head">
          <h3>{t("The usual week")}</h3>
          {canWrite ? <AddButton label={t("The usual week")} expanded={addingPattern} onClick={() => setAddingPattern(!addingPattern)} /> : null}
        </div>
        <PatternRows
          patterns={patterns}
          canWrite={canWrite}
          onRemove={(patternId) =>
            void mutate(
              () => send(console_.removeAvailabilityPattern(apiBase, patternId)),
              t("That pattern could not be removed."),
            )
          }
        />
        {canWrite && addingPattern ? (
          <PatternForm
            busy={busy}
            onSubmit={(payload) =>
              void mutate(
                () => send(console_.addAvailabilityPattern(apiBase, worker.id, payload)),
                t("That pattern could not be added."),
              )
            }
          />
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>{t("Dated changes")}</h3>
          {canWrite ? <AddButton label={t("Dated changes")} expanded={addingChange} onClick={() => setAddingChange(!addingChange)} /> : null}
        </div>
        <ExceptionRows
          exceptions={exceptions}
          canWrite={canWrite}
          onRemove={(exceptionId) =>
            void mutate(
              () => send(console_.removeAvailabilityException(apiBase, exceptionId)),
              t("That change could not be removed."),
            )
          }
        />
        {canWrite && addingChange ? (
          <ExceptionForm
            kinds={exception_kinds}
            busy={busy}
            onSubmit={(payload) =>
              void mutate(
                () => send(console_.addAvailabilityException(apiBase, worker.id, payload)),
                t("That change could not be added."),
              )
            }
          />
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>{t("What they asked for")}</h3>
          {canWrite ? <AddButton label={t("Change what they asked for")} expanded={editingPref} onClick={() => setEditingPref(!editingPref)} /> : null}
        </div>
        <PreferenceRows preference={preference} />
        {canWrite && editingPref ? (
          <PreferenceForm
            preference={preference}
            busy={busy}
            onCancel={() => setEditingPref(false)}
            onSubmit={(payload) =>
              void mutate(
                () => send(console_.setAvailabilityPreference(apiBase, worker.id, payload)),
                t("That preference could not be saved."),
              ).then((saved) => {
                if (saved) setEditingPref(false);
              })
            }
          />
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>{t("Other employers and their clients")}</h3>
        </div>
        {/* SHOWN, AND NOT OFFERED. The organisation may see the position of this
            switch — it is a fact about somebody on their bench — and cannot
            change it from anywhere, because the column is absent from the
            connection's UPDATE grant and a trigger refuses any writer who is not
            the worker. A rule the console cannot see is a rule a scheduler
            assumes does not exist and asks somebody to work around. */}
        <StatusChip
          tone={worker.marketplace_visible ? "live" : "neutral"}
          label={worker.marketplace_visible ? t("Visible to other employers") : t("Private")}
        />
        <p className="muted">
          {t("Only {name} can change this, from their own app. If it is on, other organisations can see whether they are free at a time — never who they are working for, and never what the work is.", { name: worker.given_name })}
        </p>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>{t("The next four weeks")}</h3>
        </div>
        <WindowRows windows={windows} />
      </section>
    </>
  );
}

// ===========================================================================
// THE WORKER APP SCREEN
// ===========================================================================
export function MyAvailability() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  // Narrowed at the point of use, as the type's own note says: `worker_ids`
  // exists only on the authenticated arm of the union.
  const workerId = (me?.authenticated ? me.worker_ids?.[0] : undefined) ?? "";
  const { loaded, windows, notice, busy, load, mutate } = useAvailability(workerId);
  const [addingPattern, setAddingPattern] = useState(false);
  const [addingChange, setAddingChange] = useState(false);
  const [editingPref, setEditingPref] = useState(false);
  const [visible, setVisible] = useState<boolean | null>(null);
  const [switchNote, setSwitchNote] = useState<string | null>(null);

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;

  if (loaded.state === "error" || loaded.state === "none") {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("My availability")}</h2>
        </div>
        {loaded.state === "none" ? (
          <Notice tone="neutral">{t("There is no bench record on this account yet.")}</Notice>
        ) : (
          <LoadFailed failure={loaded.failure} fallback={t("We could not load this availability.")} onRetry={() => void load()} />
        )}
      </section>
    );
  }

  const { worker, patterns, exceptions, preference, exception_kinds } = loaded.data;

  async function setMarketplace(next: boolean) {
    setSwitchNote(null);
    const { status, body } = await send<{
      marketplace_visible?: boolean;
      explanation?: string;
      error?: { message?: string };
    }>(console_.setMarketplaceVisibility(apiBase, next));
    if (status !== 200) {
      setSwitchNote(refusal(body.error) ?? t("That setting could not be saved."));
      return;
    }
    setVisible(body.marketplace_visible ?? next);
    // THE SENTENCE COMES FROM THE SERVER. One place composes the state and the
    // explanation of it, so a screen cannot describe a switch position the API
    // did not put it in.
    setSwitchNote(body.explanation ? translateLabel(locale, body.explanation) : null);
  }

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h2>{t("When I can work")}</h2>
        </div>
        {notice ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone="failed" label={t("Not saved")} />
            <span>{notice}</span>
          </div>
        ) : null}
        <p className="muted">
          {t(
            "Nobody is asked why. Say which days and hours you can work, and add a dated change for a day that is different.",
          )}
        </p>
      </section>

      <Tabs
        label={t("When I can work")}
        tabs={[
          {
            id: "avail-week",
            label: t("My usual week"),
            content: (
              <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h3>{t("My usual week")}</h3>
          <AddButton label={t("My usual week")} expanded={addingPattern} onClick={() => setAddingPattern(!addingPattern)} />
        </div>
        <PatternRows
          patterns={patterns}
          canWrite
          onRemove={(patternId) =>
            void mutate(
              () => send(console_.removeAvailabilityPattern(apiBase, patternId)),
              t("That pattern could not be removed."),
            )
          }
        />
        {addingPattern ? (
        <PatternForm
          busy={busy}
          onSubmit={(payload) =>
            void mutate(
              () => send(console_.addAvailabilityPattern(apiBase, workerId, payload)),
              t("That pattern could not be added."),
            )
          }
        />
        ) : null}
      </section>
      <section className="panel">
        <div className="panel-head">
          <h3>{t("My next four weeks")}</h3>
        </div>
        <WindowRows windows={windows} />
      </section>
              </div>
            ),
          },
          {
            id: "avail-changes",
            label: t("Days that are different"),
            content: (
              <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h3>{t("Days that are different")}</h3>
          <AddButton label={t("Days that are different")} expanded={addingChange} onClick={() => setAddingChange(!addingChange)} />
        </div>
        <ExceptionRows
          exceptions={exceptions}
          canWrite
          onRemove={(exceptionId) =>
            void mutate(
              () => send(console_.removeAvailabilityException(apiBase, exceptionId)),
              t("That change could not be removed."),
            )
          }
        />
        {addingChange ? (
        <ExceptionForm
          kinds={exception_kinds}
          busy={busy}
          onSubmit={(payload) =>
            void mutate(
              () => send(console_.addAvailabilityException(apiBase, workerId, payload)),
              t("That change could not be added."),
            )
          }
        />
        ) : null}
      </section>
              </div>
            ),
          },
          {
            id: "avail-wishes",
            label: t("What I asked for"),
            content: (
              <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h3>{t("What I asked for")}</h3>
          <AddButton label={t("Change what I asked for")} expanded={editingPref} onClick={() => setEditingPref(!editingPref)} />
        </div>
        <PreferenceRows preference={preference} />
        {editingPref ? (
          <PreferenceForm
            preference={preference}
            busy={busy}
            onCancel={() => setEditingPref(false)}
            onSubmit={(payload) =>
              void mutate(
                () => send(console_.setAvailabilityPreference(apiBase, workerId, payload)),
                t("That could not be saved."),
              ).then((saved) => {
                if (saved) setEditingPref(false);
              })
            }
          />
        ) : null}
      </section>
              </div>
            ),
          },
          {
            id: "avail-others",
            label: t("Other employers"),
            content: (
              <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h3>{t("Other employers")}</h3>
        </div>
        {/* THE ONE SWITCH THAT IS NOBODY'S BUT THEIRS. The agency that added
            this person cannot turn it on and cannot turn it off — not because
            this screen hides a button, but because a database trigger refuses
            any writer who is not this account. */}
        <label className="check">
          <input
            type="checkbox"
            // THE SWITCH OPENS IN THE POSITION IT IS ACTUALLY IN. `visible`
            // is null until this session changes it, and falling back to the
            // stored value rather than to `false` is the difference between a
            // screen that reports a setting and one that quietly proposes
            // turning it off. No effect and no sync: the server's value is the
            // default and local state only ever overrides it after a save.
            checked={visible ?? worker.marketplace_visible}
            onChange={(e) => void setMarketplace(e.target.checked)}
          />
          <span>{t("Let other employers on Verbunk, and the clients they book people for, see when I am free")}</span>
        </label>
        {/* ★ THE AUDIENCE IS NAMED IN FULL — D12's copy debt, closed by 0133.
            `app.marketplace_worker_is_free` answers a CLIENT's question, not
            only an agency's, and a consent that names only agencies does not
            cover what it authorises. The wording here and
            `app.consent_purpose.marketplace_listing` must both say so;
            verify-V37 §5 asserts each of them separately, because this text is
            hardcoded in the component and the ledger's is a row. */}
        <p className="muted">
          {t(
            "They see only whether you are free at a time — never who you are working for, never what the work is, and never why you are busy. A client of an agency can ask the same question and gets the same yes or no. Only you can change this. The organisation that added you cannot.",
          )}
        </p>
        {switchNote ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip
              tone={(visible ?? worker.marketplace_visible) ? "live" : "neutral"}
              label={(visible ?? worker.marketplace_visible) ? t("Visible") : t("Private")}
            />
            <span>{switchNote}</span>
          </div>
        ) : null}
      </section>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
